/**
 * Histopathology Reporting – Pre Approval List.
 * GET /histopathology/result-page/?tab=pre_approval with optional ?date=YYYY-MM-DD
 * Posting action is "Pre Approved" which stamps pre_approved_by / pre_approved_date
 * on the result and keeps status as sent_for_approval. After pre-approval the row
 * disappears from Pre Approval List; the main doctor can still see (and approve)
 * it in Approval List.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonValidation from "../../Utility/CommonValidation";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { ImagePaths } from "../../Utility/ImagePaths";
import { Colors } from "../../Styles/Colors";
import { CurrencySymbol } from "../../Utility/Constants";
import ResultEnterPopup from "./ResultEnterPopup";
import { canEditTab } from "../../Utility/Services/RoleAccessService";
import "../../Styles/Configuration.css";

function PatientDetailsCell({ data }) {
  if (!data) return null;
  const name = CommonValidation.removeSpace(data?.name ?? data?.patient_name) || "-";
  const sub = [data?.nxt_id ?? "-", data?.patients_mobile ?? data?.mobile_no].filter(Boolean).join(" | ");
  return (
    <Box sx={{ lineHeight: 1.3 }}>
      {name.length > 25 ? (
        <Tooltip title={name.toUpperCase()} placement="top" arrow>
          <Typography variant="body2" fontWeight={600} sx={{ display: "block", margin: 0, textTransform: "uppercase" }}>{name.slice(0, 20) + "..."}</Typography>
        </Tooltip>
      ) : (
        <Typography variant="body2" fontWeight={600} sx={{ display: "block", margin: 0, textTransform: "uppercase" }}>{name}</Typography>
      )}      {sub ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", margin: 0, marginTop: "2px" }}>{sub}</Typography> : null}
    </Box>
  );
}

export default function PreApprovalList() {
  const [states, setState] = useState({
    approvalList: [],
    page: 0,
    pageSize: 10,
    fromDate: DateTime.now().toFormat("yyyy-MM-dd"),
    dateIndex: 1,
    loading: false,
    isErrorMsg: false,
    isErrorMsgText: "",
    isSuccessMsg: false,
    isSuccessMsgText: "",
    signatureData: [],
    templateData: [],
    resultPopupData: null,
    resultPopupLoading: false,
    templatePopUpOpen: false,
    selectedPatient: null,
  });

  useEffect(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_ESIGN_CONFIG)
      .then((res) => {
        if (res?.data?.status === "success") {
          const list = res.data.data?.filter((item) => item.status) || [];
          setState((prev) => ({ ...prev, signatureData: list }));
        }
      })
      .catch((err) => setState((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: err?.response?.data?.message || err?.message })));
  }, []);

  useEffect(() => {
    if (!states.templatePopUpOpen || !states.selectedPatient?.bill_id) {
      if (!states.templatePopUpOpen) setState((prev) => ({ ...prev, templateData: [] }));
      return;
    }
    const billId = states.selectedPatient.bill_id;
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_TEMPLATE}?bill_id=${billId}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setState((prev) => ({ ...prev, templateData: res.data.data ?? [] }));
        } else {
          setState((prev) => ({ ...prev, templateData: [] }));
        }
      })
      .catch(() => setState((prev) => ({ ...prev, templateData: [] })));
  }, [states.templatePopUpOpen, states.selectedPatient?.bill_id]);

  useEffect(() => {
    if (!states.templatePopUpOpen || !states.selectedPatient) {
      setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
      return;
    }
    const resultDetailId = states.selectedPatient.action_id ?? states.selectedPatient.result_detail_id;
    if (!resultDetailId) {
      setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
      return;
    }
    setState((prev) => ({ ...prev, resultPopupLoading: true }));
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_POPUP}?result_detail_id=${resultDetailId}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success" && res?.data?.data) {
          setState((prev) => ({ ...prev, resultPopupData: res.data.data, resultPopupLoading: false }));
        } else {
          setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
        }
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          resultPopupData: null,
          resultPopupLoading: false,
          isErrorMsg: true,
          isErrorMsgText: err?.response?.data?.message ?? err?.message ?? "Failed to load popup data.",
        }));
      });
  }, [states.templatePopUpOpen, states.selectedPatient?.action_id, states.selectedPatient?.result_detail_id]);

  const fetchApprovalList = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));
    const date = states.fromDate || DateTime.now().toFormat("yyyy-MM-dd");
    const params = new URLSearchParams();
    params.set("tab", "pre_approval");
    if (date) params.set("date", date);
    const query = params.toString();
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_PAGE}?${query}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          const list = res.data.data || [];
          const mapped = list.map((item) => ({
            ...item,
            action_id: item.result_detail_id,
            name: item.patient_name ?? item.name,
            patients_mobile: item.mobile_no ?? item.patients_mobile,
            patient_account_number: item.patient_identifier ?? item.patient_account_number,
            net_amount: item.bill_amount ?? item.net_amount,
            balance_amount: item.balance ?? item.balance_amount,
            test_name: item.test_names ?? item.test_name,
          }));
          setState((prev) => ({ ...prev, approvalList: mapped, loading: false }));
        } else {
          setState((prev) => ({ ...prev, approvalList: [], loading: false }));
        }
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          approvalList: [],
          loading: false,
          isErrorMsg: true,
          isErrorMsgText: err?.response?.data?.message || err?.message || "Failed to load approval list",
        }));
      });
  }, [states.fromDate]);

  useEffect(() => {
    fetchApprovalList();
  }, [fetchApprovalList]);

  const errorMessage = (msg) => setState((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: msg }));
  const successMessage = (msg) => setState((prev) => ({ ...prev, isSuccessMsg: true, isSuccessMsgText: msg }));
  const msgClose = () => setState((prev) => ({ ...prev, isErrorMsg: false, isErrorMsgText: "", isSuccessMsg: false, isSuccessMsgText: "" }));

  const dataURLtoFile = (dataUrl, fileName = "capture.jpg") => {
    if (!dataUrl || typeof dataUrl !== "string") return null;
    try {
      const arr = dataUrl.split(",");
      const mime = arr[0].match(/:(.*?);/)?.[1] ?? "image/jpeg";
      const bstr = atob(arr[1] ?? "");
      const u8arr = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
      return new File([u8arr], fileName, { type: mime });
    } catch {
      return null;
    }
  };

  const rejectResult = (reason) => {
    const row = states.selectedPatient;
    const resultDetailId = row?.action_id ?? row?.result_detail_id;
    if (!resultDetailId) {
      errorMessage("Invalid row. Please try again.");
      return;
    }
    const formData = new FormData();
    formData.append("result_detail_id", resultDetailId);
    formData.append("status", "Rejected");
    formData.append("reject_reason", reason || "");
    setState((prev) => ({ ...prev, loading: true }));
    RestAPIService.postFormData(formData, Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_SAVE)
      .then((res) => {
        if (res?.data?.status === "success") {
          successMessage(res?.data?.message || "Result rejected. Moved back to Pending.");
          setState((prev) => ({
            ...prev,
            loading: false,
            templatePopUpOpen: false,
            selectedPatient: null,
            resultPopupData: null,
          }));
          fetchApprovalList();
        } else {
          setState((prev) => ({ ...prev, loading: false }));
          errorMessage(res?.data?.message || "Reject failed.");
        }
      })
      .catch((err) => {
        setState((prev) => ({ ...prev, loading: false }));
        errorMessage(err?.response?.data?.message || err?.message || "Reject failed.");
      });
  };

  const postResultTemplateData = (resultHtml, selectedLabIncharge, selectedPathologist2, options = {}) => {
    if (!resultHtml?.replace(/<[^>]*>/g, "").trim()) {
      errorMessage("Please enter Result content.");
      return;
    }
    const row = states.selectedPatient;
    const resultDetailId = row?.action_id ?? row?.result_detail_id;
    if (!resultDetailId) {
      errorMessage("Invalid row. Please try again.");
      return;
    }
    const status = options.isForCompleted ? "Pre Approved" : "save";
    const formData = new FormData();
    formData.append("result_detail_id", resultDetailId);
    formData.append("status", status);
    formData.append("report_content", resultHtml?.trim() ?? "");
    formData.append("esign_left_id", selectedLabIncharge?.id ?? "");
    formData.append("esign_right_id", selectedPathologist2?.id ?? "");
    if (Array.isArray(options.pdfUploadList) && options.pdfUploadList.length > 0) {
      options.pdfUploadList.forEach((item) => {
        const file = item?.document_file ?? item?.file ?? item;
        const name = item?.document_name ?? item?.name ?? "file";
        if (file instanceof Blob || file instanceof File) {
          formData.append("result_images", file, name);
        }
      });
    }
    if (Array.isArray(options.result_document) && options.result_document.length > 0) {
      formData.append("result_document", JSON.stringify(options.result_document));
    }
    setState((prev) => ({ ...prev, loading: true }));
    RestAPIService.postFormData(formData, Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_SAVE)
      .then((res) => {
        if (res?.data?.status === "success") {
          successMessage(res?.data?.message ?? (options.isForCompleted ? "Result approved." : "Result saved."));
          setState((prev) => ({
            ...prev,
            loading: false,
            templatePopUpOpen: false,
            selectedPatient: null,
            resultPopupData: null,
          }));
          fetchApprovalList();
        } else {
          setState((prev) => ({ ...prev, loading: false }));
          errorMessage(res?.data?.message ?? "Save failed.");
        }
      })
      .catch((err) => {
        setState((prev) => ({ ...prev, loading: false }));
        errorMessage(err?.response?.data?.message ?? err?.message ?? "Failed to save result.");
      });
  };

  const rows = useMemo(() => {
    const list = states.approvalList || [];
    return list.map((el, i) => ({
      ...el,
      sno: i + 1,
      patientdetils: `${CommonValidation.removeSpace(el?.name) || ""} ${el?.patients_mobile || ""} ${el?.patient_account_number || ""}`,
    }));
  }, [states.approvalList]);

  const columns = useMemo(
    () => [
      {
        field: "overall_sample_id",
        headerName: "Unique ID",
        flex: 0.1,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.8dvw" }}>{params?.row?.overall_sample_id ?? "-"}</Box>
        ),
      },
      {
        field: "sample_id",
        headerName: "Biopsy Number",
        flex: 0.11,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.8dvw" }}>{params?.row?.sample_id ?? "-"}</Box>
        ),
      },
      {
        field: "patientdetils",
        headerName: "Patient Name",
        flex: 0.2,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box>
            <PatientDetailsCell data={params?.row} />
          </Box>
        ),
      },
      {
        field: "test_name",
        headerName: "Test Name",
        flex: 0.17,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ pt: 1, fontSize: "0.8dvw" }}>
            {params?.row?.test_name?.length > 30 ? (
              <Tooltip title={params.row.test_name} placement="top" arrow>
                <Box component="span">{params.row.test_name.slice(0, 30) + "..."}</Box>
              </Tooltip>
            ) : (
              <Box>{params?.row?.test_name || "-"}</Box>
            )}
          </Box>
        ),
      },
      {
        field: "specimen",
        headerName: "Specimen",
        flex: 0.12,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const val = params?.row?.specimen ?? "-";
          return (
            <Tooltip title={val} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: "0.8dvw",
                  width: "100%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {val}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "net_amount",
        headerName: "Payment Status",
        flex: 0.17,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box mt={"0.5vw"}>
            <Typography variant="body2" sx={{ fontSize: "0.8dvw" }}>
              Bill : {CommonValidation.AmountFormat(params.row.net_amount)}
            </Typography>
            <Typography variant="body2" color="error" sx={{ fontSize: "0.8dvw" }}>
              Balance : {CommonValidation.AmountFormat(params.row.balance_amount)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "submitted_by",
        headerName: "Submitted By",
        flex: 0.12,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.8dvw" }}>{params?.row?.submitted_by ?? "-"}</Box>
        ),
      },
      {
        field: "action",
        headerName: "Actions",
        headerAlign: "left",
        align: "left",
        flex: 0.1,
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            sx={{ minWidth: 40 }}
            onClick={() =>
              setState((prev) => ({
                ...prev,
                templatePopUpOpen: true,
                selectedPatient: params?.row,
              }))
            }
          >
            <Box component="img" sx={{ width: "1.5dvw", height: "1.5dvw" }} src={ImagePaths.Result?.default ?? ImagePaths.Result} alt="result" />
          </IconButton>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ width: "100%", height: "80vh", backgroundColor: Colors?.Background, padding: "0.5vw", boxSizing: "border-box" }}>
      <Box
        className="SpecimenCollection-page"
        sx={{ width: "100%", height: "78vh", backgroundColor: "white", display: "flex", flexDirection: "column", overflow: "hidden", boxSizing: "border-box" }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: "relative",
            zIndex: 10,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            p: 1.5,
            backgroundColor: "#fafafa",
            borderBottom: "1px solid #e0e0e0",
            minHeight: "8.5vh",
            flexShrink: 0,
            overflow: "visible",
          }}
        >
          <TextField
            size="small"
            type="date"
            label="Date"
            value={states.fromDate}
            onChange={(e) => setState((prev) => ({ ...prev, fromDate: e.target.value || DateTime.now().toFormat("yyyy-MM-dd") }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: "10vw" }}
          />
        </Box>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => String(row.action_id ?? row.result_detail_id ?? row.bill_id ?? row.patient_id ?? row.sno ?? "")}
          paginationModel={{ page: states.page, pageSize: states.pageSize }}
          onPaginationModelChange={(m) => setState((prev) => ({ ...prev, page: m.page, pageSize: m.pageSize }))}
          pageSizeOptions={[10, 20, 30]}
          pagination
          rowHeight={52}
          columnHeaderHeight={40}
          disableRowSelectionOnClick
          loading={states.loading}
          density="standard"
          showToolbar
          localeText={{
            toolbarColumns: "",
            toolbarDensity: "",
            toolbarExport: "",
            toolbarFilters: "",
            toolbarExportPrint: "",
          }}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                No Records To Be Shown
              </Box>
            ),
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            position: "relative",
            zIndex: 1,
            "& .MuiDataGrid-toolbarContainer": { position: "relative", zIndex: 5 },
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, color: "#616161" },
            "& .MuiDataGrid-cell": {},
          }}
        />
      </Box>

      <ResultEnterPopup
        open={!!states.templatePopUpOpen}
        showRejectButton={true}
        onReject={(reason) => rejectResult(reason)}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            templatePopUpOpen: false,
            selectedPatient: null,
            resultPopupData: null,
          }))
        }
        signatureData={states.signatureData || []}
        templateData={states.resultPopupData?.template_options ?? states.templateData ?? []}
        popupData={states.resultPopupData}
        popupLoading={states.resultPopupLoading}
        errorMessage={errorMessage}
        onSave={postResultTemplateData}
        selectedServiceData={states.selectedPatient}
        resultTextData={states.selectedPatient?.result_data}
        editorDisable={
          !canEditTab("Pre Approval List") ||
          (states.resultPopupData
            ? (states.resultPopupData.status === "Completed" || states.resultPopupData.status === "Dispatched" ||
              (states.resultPopupData.status || "").toLowerCase() === "completed" ||
              (states.resultPopupData.status || "").toLowerCase() === "dispatched" ||
              (states.resultPopupData.status || "").toLowerCase() === "approved")
            : (states.selectedPatient?.result_status === "Completed" || states.selectedPatient?.result_status === "Dispatched"))
        }
        isDispatchshown={false}
        postButtonLabel="Pre Approve"
        showOnlyApproveButton
      />

      {states.isErrorMsg && <ToastMsg severity="error" msg={states.isErrorMsgText} msgPop={msgClose} />}
      {states.isSuccessMsg && <ToastMsg severity="success" msg={states.isSuccessMsgText} msgPop={msgClose} />}
    </Box>
  );
}
